"use client";
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import NextImage from "next/image";
import { ImageContainerProps } from "./types";

const ImageContainer: React.FC<ImageContainerProps> = ({
  src,
  alt,
  description,
  title,
  position = "",
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const isRightSide = position.includes("ml-auto");
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const existingPortal = document.getElementById("portal-root");
    if (existingPortal) {
      setPortalRoot(existingPortal);
    } else {
      const div = document.createElement("div");
      div.id = "portal-root";
      div.style.position = "fixed";
      div.style.left = "0";
      div.style.top = "0";
      div.style.width = "100%";
      div.style.height = "100%";
      div.style.pointerEvents = "none";
      div.style.zIndex = "9999";
      document.body.appendChild(div);
      setPortalRoot(div);
    }
  }, []);

  const updatePosition = React.useCallback(() => {
    if (isHovered && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const scale = 1.1;
      const imageWidth = rect.width * scale;
      const imageHeight = rect.height * scale;
      const scaledOffsetY = (imageHeight - rect.height) / 2;
      const scaledOffsetX = (imageWidth - rect.width) / 2;

      let xPos;
      if (isRightSide) {
        // For right-aligned images, account for the right-aligned transform origin
        xPos = rect.x - 441;
      } else {
        // For left/center-aligned images, account for the increased width
        xPos = rect.x + rect.width + scaledOffsetX;
      }

      setCoords({
        x: isRightSide ? xPos : xPos + 24,

        y: rect.y - scaledOffsetY,
      });
    }
  }, [isHovered, isRightSide]);

  useEffect(() => {
    if (isHovered) {
      updatePosition();
      window.addEventListener("scroll", updatePosition);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isHovered, updatePosition]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-[350px] h-[250px] ${position}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className="relative w-[350px] h-[250px] overflow-hidden rounded-lg"
        animate={{
          scale: isHovered ? 1.1 : 1,
        }}
        transition={{
          duration: 0.4,
          ease: "easeOut",
        }}
        style={{
          transformOrigin: isRightSide ? "right center" : "left center",
        }}
      >
        <NextImage src={src} alt={alt} fill className="object-cover" />
      </motion.div>

      {portalRoot &&
        isHovered &&
        createPortal(
          <AnimatePresence>
            <motion.div
              className="fixed h-[276.5px] pointer-events-auto border-2 border-t-gray-400/60 border-l-gray-400/60 border-b-gray-500 border-r-gray-500 bg-[rgba(65,59,59,0.7)] backdrop-blur-[27.5px] text-white p-6 rounded-lg w-[400px]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{
                duration: 0.3,
                ease: "easeOut",
              }}
              style={{
                left: coords.x,
                top: coords.y,
              }}
            >
              <h3 className="text-2xl font-medium mb-2">{title}</h3>
              <p className="text-[15.5px] text-justify font-light">
                {description}
              </p>
            </motion.div>
          </AnimatePresence>,
          portalRoot
        )}
    </div>
  );
};

export default ImageContainer;
